import React, { useState, useEffect, memo } from 'react';
import { Map, fromJS } from 'immutable';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';

import Popup from '../../helpers/Popup';
import Button from '../../helpers/Button';
import BaseInput from '../../helpers/Input';
import FormGroup from '../../helpers/FormGroup';
import useRequiredFieldErrors from '../../../hooks/useRequiredFieldErrors';
import FormErrors from '../../helpers/FormErrors';
import config from '../../../config';
import { InputProps } from '../../helpers/AutoComplete';
import AutoComplete from '../../../containers/AutoComplete';
import PrettyError from '../../decorators/PrettyError';
import DisplayError from '../../decorators/DisplayError';
import BaseSelect from '../../helpers/Select';
import { dashboardDates } from '../../../selectors/dashboard';
import { changeField } from '../../../actions/dashboards';
import { usePopupContext } from '../../../contexts/PopupContext';

import multiselectItemStyle from '../../helpers/MultiselectItem/style.module.css';
import style from './style.module.css';

const Input = DisplayError(BaseInput);

const Select = PrettyError(DisplayError(BaseSelect));

const PROJECT_TYPE_OPTIONS = fromJS([
  { value: 'Both', name: 'Both' },
  { value: 'Platform', name: 'Platform' },
  { value: 'Add-on', name: 'Add-on' },
]);

const TYPES = {
  module: 'module',
  user: 'user',
};

/** Retainer health report popup component. */
const RetainerHealthPopup = props => {
  const { closePopup } = usePopupContext();
  const { companyInfoData, findUsers, findModules, clearUsers, user } = props;
  const dates = useSelector(dashboardDates);
  const { errors: requiredErrors, formProps } = useRequiredFieldErrors();
  const dispatch = useDispatch();

  const role = user.getIn(['roles', 0, 'slug']);

  const [projectType, setProjectType] = useState(PROJECT_TYPE_OPTIONS.getIn([0, 'value']));
  const [current, setCurrent] = useState(undefined);
  const [currentOption, setCurrentOption] = useState(undefined);
  const [selectedModule, setSelectedModule] = useState([]);
  const [selectedUser, setSelectedUser] = useState([]);
  const [suggestModuleValue, setSuggestModuleValue] = useState('');
  const [suggestUserValue, setSuggestUserValue] = useState('');
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    setFormError(null);
  }, [current, currentOption]);

  const handleSubmit = event => {
    event.preventDefault();

    if (isFormValid()) {
      const from = dates.get('from').format('YYYY-MM-DD');
      const to = dates.get('to').format('YYYY-MM-DD');

      const selectedModuleQuery =
        selectedModule.length > 0 ? `&module_ids=${selectedModule.map(item => item.id).join(',')}` : '';

      const selectedModuleText =
        selectedModule.length > 0 ? `&module_text=${selectedModule.map(item => item.name).join(',')}` : '';

      const selectedUserQuery =
        selectedUser.length > 0 ? `&user_ids=${selectedUser.map(item => item.id).join(',')}` : '';

      const selectedUserText =
        selectedUser.length > 0 ? `&user_text=${selectedUser.map(item => item.name).join(',')}` : '';

      const dataFromQuery = current === 'dateRange' ? `&from=${from}` : '';
      const dataToQuery = current === 'dateRange' ? `&to=${to}` : '';
      const currentToQuery = current === 'current' ? `&current=${currentOption}` : '';

      window.open(
        `/retainer-health-report?project_type=${projectType}${currentToQuery}${selectedModuleQuery}${selectedModuleText}${selectedUserQuery}${selectedUserText}${dataFromQuery}${dataToQuery}`,
        '_blank',
      );

      closePopup();
    }
  };

  /** Check form is valid or not. */
  const isFormValid = () => {
    setFormError(null);

    if (!current) {
      setFormError('Please select Current or Date Range module.');

      return false;
    }

    if (current === 'current' && !currentOption) {
      setFormError('Please select retainer or active.');

      return false;
    }

    if (selectedModule.length === 0) {
      setFormError('Please select a module.');

      return false;
    }

    return true;
  };

  const handleCurrentChange = event => {
    setCurrent(event.target.value);
    setCurrentOption(undefined);
    clearUsers();
  };

  const handleCurrentOptionChange = event => {
    setCurrentOption(event.target.value);
  };

  const handleSuggestChange = ({ name, value }, type) => {
    if (name === 'suggestText' && type === TYPES.module) {
      setSuggestModuleValue(value);
    }

    if (name === 'suggestText' && type === TYPES.user) {
      setSuggestUserValue(value);
    }
  };

  const handleSuggestSelect = ({ id, text }, type) => {
    if (type === TYPES.module) {
      const isSameModule = selectedModule.find(include => include.id === id);

      if (isSameModule === undefined) {
        setSelectedModule([...selectedModule, { id, name: text }]);
      }
      setSuggestModuleValue('');
    }

    if (type === TYPES.user) {
      const isSameUser = selectedUser.find(include => include.id === id);

      if (isSameUser === undefined) {
        setSelectedUser([...selectedUser, { id, name: text }]);
      }
      setSuggestUserValue('');
    }
  };

  const handleRemoveSelected = (event, type) => {
    const { name } = event.target;
    const id = parseInt(name.replace('include_', ''), 10);

    if (type === TYPES.module) {
      setSelectedModule(selectedModule.filter(item => item.id !== id));
    }

    if (type === TYPES.user) {
      setSelectedUser(selectedUser.filter(item => item.id !== id));
    }
  };

  const handleDateChange = event => {
    const { name: field, value } = event.target;

    dispatch(changeField({ field, value }));
  };

  const header = 'Retainer Health Report';
  const footer = (
    <Button className="btn-primary" onClick={handleSubmit} type="button">
      Generate
    </Button>
  );

  const handleProjectTypeChange = event => {
    const { value } = event.target;

    setProjectType(value);
  };

  const renderSelectedModule =
    selectedModule.length > 0 &&
    selectedModule.map(include => (
      <div key={include.id} className={multiselectItemStyle.host}>
        {include.name}
        <button
          aria-label="close"
          className={classNames(multiselectItemStyle.close, 'close')}
          name={`include_${include.id}`}
          onClick={event => handleRemoveSelected(event, TYPES.module)}
          type="button"
        >
          &times;
        </button>
      </div>
    ));

  const renderSelectedUser =
    selectedUser.length > 0 &&
    selectedUser.map(include => (
      <div key={include.id} className={multiselectItemStyle.host}>
        {include.name}
        <button
          aria-label="close"
          className={classNames(multiselectItemStyle.close, 'close')}
          name={`include_${include.id}`}
          onClick={event => handleRemoveSelected(event, TYPES.user)}
          type="button"
        >
          &times;
        </button>
      </div>
    ));

  const currentClassName = classNames('ml20', {
    hide: current === 'dateRange' || current === undefined,
  });

  const dateRangeClassName = classNames('ml20', {
    hide: current === 'current' || current === undefined,
  });

  return (
    <Popup className={style.host} footer={footer} header={header} id="RetainerHealthPopup">
      <form onSubmit={handleSubmit} {...formProps}>
        <FormGroup>
          <div className="radio-wrap radio-wrap--reverse">
            <label htmlFor="current">Current</label>
            <input
              checked={current === 'current'}
              id="current"
              name="current"
              onChange={handleCurrentChange}
              type="radio"
              value="current"
            />
          </div>
        </FormGroup>
        <div className={currentClassName}>
          <FormGroup>
            <div className="radio-wrap radio-wrap--reverse">
              <label htmlFor="retainer">
                Retainer: only include projects where there is an active Retainer Agreement
              </label>
              <input
                checked={currentOption === 'retainer'}
                id="retainer"
                name="currentOption"
                onChange={handleCurrentOptionChange}
                type="radio"
                value="retainer"
              />
            </div>
          </FormGroup>
          <FormGroup>
            <div className="radio-wrap radio-wrap--reverse">
              <label htmlFor="active">Active: include all &quot;Active&quot; projects for the indicated User(s)</label>
              <input
                checked={currentOption === 'active'}
                id="active"
                name="currentOption"
                onChange={handleCurrentOptionChange}
                type="radio"
                value="active"
              />
            </div>
          </FormGroup>
        </div>
        <FormGroup>
          <div className="radio-wrap radio-wrap--reverse">
            <label htmlFor="dateRange">
              Date Range: only include projects where there was an active Retainer Agreement during that range
            </label>
            <input
              checked={current === 'dateRange'}
              id="dateRange"
              name="current"
              onChange={handleCurrentChange}
              type="radio"
              value="dateRange"
            />
          </div>
        </FormGroup>
        <div className={dateRangeClassName}>
          <div className="row">
            <FormGroup className="col-sm-6">
              <Input
                error={requiredErrors['popupDates.from']}
                label="From"
                maxDate={dates.get('to')}
                name="popupDates.from"
                onChange={handleDateChange}
                placeholder="From"
                type="date"
                value={dates.get('from')}
                required
                showLabel
              />
            </FormGroup>

            <FormGroup className="col-sm-6">
              <Input
                error={requiredErrors['popupDates.to']}
                label="To"
                minDate={dates.get('from')}
                name="popupDates.to"
                onChange={handleDateChange}
                placeholder="To"
                type="date"
                value={dates.get('to')}
                required
                showLabel
              />
            </FormGroup>
          </div>
        </div>

        {![config.ANALYST_ASSOCIATE].includes(role) && (
          <>
            <FormGroup className="w260 mb0">
              <label className="field-label">Module</label>
              <InputProps className="form-control" placeholder="Module">
                <AutoComplete
                  highlightFirst={false}
                  idName="suggestId"
                  onChange={event => handleSuggestChange(event, TYPES.module)}
                  onFind={findModules}
                  onSelectCallback={event => handleSuggestSelect(event, TYPES.module)}
                  suggestions={companyInfoData.get('suggests')}
                  value={suggestModuleValue}
                  valueName="suggestText"
                />
              </InputProps>
            </FormGroup>
            <div className="d-flex flex-wrap">{renderSelectedModule}</div>
          </>
        )}

        <FormGroup className="w260 mb0">
          <label className="field-label">Users</label>
          <InputProps className="form-control" placeholder="User">
            <AutoComplete
              highlightFirst={false}
              idName="suggestId"
              onChange={event => handleSuggestChange(event, TYPES.user)}
              onFind={findUsers}
              onSelectCallback={event => handleSuggestSelect(event, TYPES.user)}
              suggestions={companyInfoData.get('suggests')}
              value={suggestUserValue}
              valueName="suggestText"
            />
          </InputProps>
        </FormGroup>
        <div className="d-flex flex-wrap">{renderSelectedUser}</div>

        <FormGroup className="w260">
          <label className="field-label">Project Type</label>
          <Select
            id="projectType"
            label="Project Type"
            name="projectType"
            onChange={handleProjectTypeChange}
            options={PROJECT_TYPE_OPTIONS}
            selectClassName="input-sm"
            value={projectType}
          />
        </FormGroup>

        <FormErrors className="block-center mb0 mt15 error-text" errors={formError} />
      </form>
    </Popup>
  );
};

RetainerHealthPopup.propTypes = {
  clearUsers: PropTypes.func.isRequired,
  companyInfoData: PropTypes.instanceOf(Map).isRequired,
  findModules: PropTypes.func.isRequired,
  findUsers: PropTypes.func.isRequired,
};

export default memo(RetainerHealthPopup);
